import {
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  theme as antdTheme,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { reportsApi, type CashBookRow } from '@/api/reports';
import { financeApi } from '@/api/finance';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const CATEGORY_COLOR: Record<string, string> = {
  'Fee received': 'green',
  'Other income': 'green',
  'Store sale': 'green',
  'Store credit received': 'green',
  Expense: 'red',
  'Salary paid': 'red',
  'Salary advance': 'red',
  'Vendor payment': 'red',
  'Store purchase': 'red',
  'Inventory purchase': 'red',
  'Handed to Principal': 'volcano',
};

export function CashBookReportPage() {
  const { token } = antdTheme.useToken();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [bankAccountId, setBankAccountId] = useState<number | undefined>();

  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');

  const { data: accounts } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'cash-book', from, to, bankAccountId],
    queryFn: () => reportsApi.cashBook(from, to, bankAccountId),
  });

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Reports"
        title="Cash book"
        description="Every rupee in and out for the selected period, in plain language — who paid, what was spent — with a running balance. Cancelled (reversed) entries are excluded."
        actions={
          <ExportButton
            items={[{
              kind: 'pdf',
              url: '/reports/exports/cash-book.pdf',
              params: { from, to, bankAccountId },
              fileName: `cash-book-${from}-to-${to}.pdf`,
              disabled: !data,
            }]}
          />
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        <Select
          style={{ minWidth: 220 }}
          placeholder="All money accounts"
          allowClear
          value={bankAccountId}
          onChange={setBankAccountId}
          options={(accounts ?? []).map((a) => ({ value: a.id, label: a.accountName }))}
        />
      </Space>

      {data ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Opening balance" value={PKR(data.openingBalance)} prefix="₨" />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Money in" value={PKR(data.totalIn)} prefix="₨" styles={{ content: { color: token.colorSuccess } }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Money out" value={PKR(data.totalOut)} prefix="₨" styles={{ content: { color: token.colorError } }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Closing balance" value={PKR(data.closingBalance)} prefix="₨" />
              </Card>
            </Col>
          </Row>

          {data.byCategory.length > 0 && (
            <Card title="Where the money came from / went" styles={{ body: { padding: 0 } }}>
              <Table
                rowKey="category"
                size="small"
                pagination={false}
                dataSource={data.byCategory}
                columns={[
                  {
                    title: 'Type',
                    dataIndex: 'category',
                    render: (c: string) => <Tag color={CATEGORY_COLOR[c]}>{c}</Tag>,
                  },
                  {
                    title: 'Money in',
                    dataIndex: 'moneyIn',
                    width: 160,
                    align: 'right',
                    render: (v: string) =>
                      Number(v) === 0 ? '—' : <Typography.Text style={{ color: token.colorSuccess }}>₨ {PKR(v)}</Typography.Text>,
                  },
                  {
                    title: 'Money out',
                    dataIndex: 'moneyOut',
                    width: 160,
                    align: 'right',
                    render: (v: string) =>
                      Number(v) === 0 ? '—' : <Typography.Text style={{ color: token.colorError }}>₨ {PKR(v)}</Typography.Text>,
                  },
                ]}
              />
            </Card>
          )}

          <Card title="Day-by-day movements" styles={{ body: { padding: 0 } }}>
            {data.rows.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No money moved in this period" />
            ) : (
              <Table<CashBookRow>
                rowKey={(_, i) => String(i)}
                size="small"
                loading={isFetching}
                pagination={{ pageSize: 50, showSizeChanger: false }}
                dataSource={data.rows}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    width: 110,
                    render: (d: string) => dayjs(d).format('DD MMM YY'),
                  },
                  {
                    title: 'Type',
                    dataIndex: 'category',
                    width: 150,
                    render: (c: string) => <Tag color={CATEGORY_COLOR[c]}>{c}</Tag>,
                  },
                  {
                    title: 'Details',
                    render: (_, r) => (
                      <>
                        <Typography.Text>{r.particulars || '—'}</Typography.Text>
                        {(r.reference || (!bankAccountId && r.account)) && (
                          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                            {[r.reference, !bankAccountId ? r.account : null].filter(Boolean).join(' · ')}
                          </Typography.Text>
                        )}
                      </>
                    ),
                  },
                  {
                    title: 'Money in',
                    dataIndex: 'moneyIn',
                    width: 130,
                    align: 'right',
                    render: (v: string | null) =>
                      v == null ? '' : <Typography.Text strong style={{ color: token.colorSuccess }}>₨ {PKR(v)}</Typography.Text>,
                  },
                  {
                    title: 'Money out',
                    dataIndex: 'moneyOut',
                    width: 130,
                    align: 'right',
                    render: (v: string | null) =>
                      v == null ? '' : <Typography.Text strong style={{ color: token.colorError }}>₨ {PKR(v)}</Typography.Text>,
                  },
                  {
                    title: 'Balance',
                    dataIndex: 'balance',
                    width: 140,
                    align: 'right',
                    render: (v: string) => `₨ ${PKR(v)}`,
                  },
                ]}
              />
            )}
          </Card>
        </>
      ) : (
        <Empty />
      )}
    </div>
  );
}
